import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { AddCircleOutline, ChevronRight } from '@mui/icons-material';
import StopMotionImage from 'components/play/StopMotionImage';
import { exerciseTypeLabel, locationLabel, muscleLabel } from 'components/play/constants';

/**
 * OTRIX Play — fila del catálogo estilo "pista de una biblioteca de canciones": miniatura
 * stop-motion (anima SOLO en hover/focus para no mover 50 a la vez), nombre, especificaciones
 * y acción única de agregar. La fila entera abre el detalle (técnica).
 */
function PlayCatalogExerciseRow({ exercise, onAdd, onOpenDetail }) {
  const { t } = useTranslation(['play']);
  const [hover, setHover] = useState(false);

  const specs = [
    exercise.exercise_type ? exerciseTypeLabel(t, exercise.exercise_type) : null,
    exercise.suggested_sets && exercise.suggested_reps
      ? `${exercise.suggested_sets} × ${exercise.suggested_reps}`
      : null,
    exercise.location ? locationLabel(t, exercise.location) : null
  ].filter(Boolean);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={exercise.name}
      onClick={() => onOpenDetail(exercise)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetail(exercise);
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        p: 1.25,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.content',
        cursor: 'pointer',
        transition:
          'transform 200ms cubic-bezier(0.32,0.72,0,1), background-color 200ms cubic-bezier(0.32,0.72,0,1), border-color 200ms cubic-bezier(0.32,0.72,0,1)',
        '&:hover': {
          backgroundColor: 'surface.hover',
          borderColor: 'hairline.strong',
          transform: 'translateY(-2px)'
        },
        '&:active': { transform: 'scale(0.98)' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
      }}>
      {/* Miniatura 9:16 */}
      <Box sx={{ width: 52, flex: 'none' }}>
        <StopMotionImage
          frame1={exercise.image_url_1}
          frame2={exercise.image_url_2}
          alt={exercise.name}
          height={88}
          playing={hover}
        />
      </Box>

      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={800} noWrap>
          {exercise.localized_name || exercise.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
          {[muscleLabel(t, exercise.muscle_group), ...specs].filter(Boolean).join(' · ')}
        </Typography>
      </Stack>

      <Button
        size="small"
        startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
        onClick={(e) => {
          e.stopPropagation();
          onAdd(exercise);
        }}
        sx={{
          flex: 'none',
          color: 'gold.main',
          fontWeight: 700,
          textTransform: 'none',
          display: { xs: 'none', sm: 'inline-flex' },
          '&:active': { transform: 'scale(0.98)' }
        }}>
        {t('browser.add', { ns: 'play', defaultValue: 'Agregar' })}
      </Button>
      <ChevronRight sx={{ color: 'text.disabled', flex: 'none', display: { sm: 'none' } }} />
    </Box>
  );
}

PlayCatalogExerciseRow.propTypes = {
  exercise: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  onOpenDetail: PropTypes.func.isRequired
};

export default PlayCatalogExerciseRow;
